import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useOnboarding } from "../context/OnboardingContext";
import { useMembership } from "../context/MembershipContext";
import MenuOptions from "../components/MenuOptions";
import ShopLocation from "../components/ShopLocation";
import OpeningHours from "../components/OpeningHours";
import WarehouseOption from "../components/WarehouseOption";

/**
 * How the shop presents itself, and the QR code that brings customers to it.
 *
 * The QR encodes the shop's slug, which is why the slug is not editable here:
 * a printed card on a counter cannot be recalled, and changing the slug would
 * quietly break every one already in the wild.
 */
const API_ROOT = process.env.REACT_APP_API_URL || "http://localhost:3010/api";
const MENU_ROOT =
  process.env.REACT_APP_MENU_URL || window.location.origin.replace(/\/$/, "");

const assetUrl = (path) => (path ? `${API_ROOT}${path}` : null);

/** Lowercases and strips anything but letters, digits and hyphens as the owner types. */
const sanitizeSlugInput = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const isValidSlug = (value) =>
  value.length >= 3 && value.length <= 64 && SLUG_PATTERN.test(value);

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ],
  );

const ShopProfile = () => {
  const { t, lang, dir } = useI18n();
  const { markStep } = useOnboarding();
  const { flag } = useMembership();
  const [shop, setShop] = useState(null);
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [qr, setQr] = useState(null);
  const cardRef = useRef(null);

  const [slugEditing, setSlugEditing] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [slugBusy, setSlugBusy] = useState(false);
  const [slugError, setSlugError] = useState("");
  // 0 = no dialog, 1 = "this will break saved links", 2 = final "are you sure"
  const [slugConfirmStep, setSlugConfirmStep] = useState(0);

  const menuUrl = shop ? `${MENU_ROOT}/s/${shop.slug}` : "";

  const load = useCallback(async () => {
    try {
      const { data } = await shopsAPI.current();
      setShop(data.shop);
      setForm({
        name: data.shop.name || "",
        phone: data.shop.phone || "",
        email: data.shop.email || "",
        address: data.shop.address || "",
        currency: data.shop.currency || "TND",
        tax_rate: data.shop.tax_rate ?? 0,
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("shop.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Big and heavily error-correcting: this gets printed, taped to a counter,
  // and scanned in poor light by whatever phone the customer happens to own.
  useEffect(() => {
    if (!shop) return;

    QRCode.toDataURL(menuUrl, {
      width: 640,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then(setQr)
      .catch(() => setQr(null));
  }, [shop, menuUrl]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      if (logo) {
        const body = new FormData();
        Object.entries(form).forEach(([key, value]) => body.append(key, value));
        body.append("logo", logo);
        await shopsAPI.update(body);
      } else {
        await shopsAPI.update(form);
      }

      setLogo(null);
      setNotice(t("common.saved"));
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("shop.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const startSlugEdit = () => {
    setSlugInput(shop.slug);
    setSlugError("");
    setSlugEditing(true);
  };

  const cancelSlugEdit = () => {
    setSlugEditing(false);
    setSlugConfirmStep(0);
    setSlugError("");
  };

  const submitSlug = async () => {
    setSlugBusy(true);
    setSlugError("");

    try {
      await shopsAPI.updateSlug(slugInput);
      setSlugEditing(false);
      setSlugConfirmStep(0);
      setNotice(t("shop.linkSaved"));
      await load();
    } catch (err) {
      setSlugConfirmStep(0);
      const status = err.response?.status;
      setSlugError(
        status === 409
          ? t("shop.linkTaken")
          : status === 400
            ? t("shop.linkInvalidFormat")
            : err.response?.data?.message || t("shop.saveFailed"),
      );
    } finally {
      setSlugBusy(false);
    }
  };

  const trySaveSlug = (event) => {
    event.preventDefault();
    if (!isValidSlug(slugInput)) {
      setSlugError(t("shop.linkInvalidFormat"));
      return;
    }
    if (slugInput === shop.slug) {
      setSlugEditing(false);
      return;
    }

    // Nothing can have been printed for a link that has never been
    // customized before, so the first pick skips the scary warnings.
    if (shop.custom_slug_set_at) {
      setSlugConfirmStep(1);
    } else {
      submitSlug();
    }
  };

  const printCard = () => {
    // Only the owner can say the card was printed, so this is the one step ticked by hand
    markStep("menu_qr");
    const card = cardRef.current;
    if (!card) return;

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);

    // The card prints in the language the owner is reading it in
    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html lang="${lang}" dir="${dir}"><head><title>${escapeHtml(
      shop.name,
    )}</title>
      <style>
        @page { size: A5; margin: 12mm; }
        body { font-family: system-ui, -apple-system, "Segoe UI", Tahoma, sans-serif; text-align: center;
               display: flex; align-items: center; justify-content: center;
               min-height: 90vh; margin: 0; }
        img.logo { max-height: 70px; margin-bottom: 12px; }
        h1 { font-size: 26px; margin: 0 0 4px; }
        p.sub { color: #475569; margin: 0 0 22px; font-size: 15px; }
        img.qr { width: 74mm; height: 74mm; }
        p.url { font-family: ui-monospace, monospace; font-size: 12px;
                color: #64748b; margin-top: 14px; word-break: break-all; direction: ltr; }
      </style></head><body>${card.innerHTML}</body></html>`);
    doc.close();

    frame.contentWindow.focus();
    frame.contentWindow.print();

    // Give the print dialog time to take its copy before the frame goes
    setTimeout(() => document.body.removeChild(frame), 1000);
  };

  if (loading || !shop) {
    return (
      <div className="flex items-center justify-center h-96">
        {error ? (
          <p className="text-red-700">{error}</p>
        ) : (
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t("shop.title")}
        </h1>
        <p className="text-gray-600">{t("shop.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <form
          onSubmit={save}
          className="bg-white rounded-xl shadow-lg p-6 space-y-4"
        >
          <div className="flex items-center gap-4">
            {shop.logo_url ? (
              <img
                src={assetUrl(shop.logo_url)}
                alt=""
                className="h-16 w-16 rounded-xl object-cover border border-gray-200"
              />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center text-2xl">
                💨
              </div>
            )}
            <label className="cursor-pointer min-w-0">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogo(e.target.files?.[0] || null)}
                className="hidden"
              />
              <span className="inline-block max-w-full truncate px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50">
                {logo
                  ? t("shop.selectedLogo", { name: logo.name })
                  : t("shop.changeLogo")}
              </span>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              {t("shop.name")}
            </span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("shop.phone")}
              </span>
              <input
                type="tel"
                dir="ltr"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg text-start"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("shop.email")}
              </span>
              <input
                type="email"
                dir="ltr"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg text-start"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              {t("shop.address")}
            </span>
            <textarea
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("shop.currency")}
              </span>
              <input
                type="text"
                maxLength={8}
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("shop.vat")}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">{t("shop.vatHint")}</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </form>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {t("shop.menuCode")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">{t("shop.menuCodeHint")}</p>

          <div
            ref={cardRef}
            className="rounded-xl border border-gray-200 p-6 text-center"
          >
            <div>
              {shop.logo_url && (
                <img
                  src={assetUrl(shop.logo_url)}
                  alt=""
                  className="logo mx-auto max-h-16 mb-3"
                />
              )}
              <h1 className="text-xl font-bold text-gray-900">{shop.name}</h1>
              <p className="sub text-gray-600 mb-5">{t("shop.scanToSee")}</p>
              {qr && (
                <img
                  src={qr}
                  alt={t("shop.qrAlt")}
                  className="qr mx-auto w-56 h-56"
                />
              )}
              <p className="url text-xs text-gray-500 mt-3 break-all" dir="ltr">
                {menuUrl}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={printCard}
              disabled={!qr}
              className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
            >
              {t("shop.print")}
            </button>
            {qr && (
              <a
                href={qr}
                download={`${shop.slug}-menu-qr.png`}
                className="px-5 py-2.5 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50"
              >
                {t("shop.download")}
              </a>
            )}
            <button
              onClick={() => {
                navigator.clipboard?.writeText(menuUrl);
                setNotice(t("shop.copied"));
              }}
              className="px-5 py-2.5 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50"
            >
              {t("shop.copyLink")}
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            {slugEditing ? (
              <form onSubmit={trySaveSlug} className="space-y-2">
                <span className="text-xs font-medium text-gray-700">
                  {t("shop.slugLabel")}
                </span>
                <input
                  type="text"
                  dir="ltr"
                  autoFocus
                  value={slugInput}
                  onChange={(e) => setSlugInput(sanitizeSlugInput(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-start"
                />
                {!shop.custom_slug_set_at && (
                  <p className="text-xs text-gray-500">
                    {t("shop.linkHintFirstTime")}
                  </p>
                )}
                {slugError && (
                  <p className="text-xs text-red-700">{slugError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={slugBusy}
                    className="px-4 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-60"
                  >
                    {slugBusy ? t("common.saving") : t("common.save")}
                  </button>
                  <button
                    type="button"
                    onClick={cancelSlugEdit}
                    disabled={slugBusy}
                    className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-xs text-gray-500 flex flex-wrap items-center gap-2">
                <span>
                  {t("shop.slugLabel")} <strong dir="ltr">{shop.slug}</strong>
                </span>
                <button
                  type="button"
                  onClick={startSlugEdit}
                  className="text-primary-700 font-semibold hover:underline"
                >
                  {t("shop.editLink")}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <MenuOptions shop={shop} onSaved={setShop} />

      {flag("sister_shops") && <ShopLocation shop={shop} onSaved={setShop} />}

      <WarehouseOption shop={shop} onSaved={setShop} />

      <OpeningHours shop={shop} onSaved={setShop} />

      {slugConfirmStep === 1 && (
        <SlugConfirmDialog
          title={t("shop.linkWarningTitle")}
          body={t("shop.linkWarningBody")}
          confirmLabel={t("shop.linkWarningContinue")}
          onCancel={() => setSlugConfirmStep(0)}
          onConfirm={() => setSlugConfirmStep(2)}
        />
      )}

      {slugConfirmStep === 2 && (
        <SlugConfirmDialog
          title={t("shop.linkFinalWarningTitle")}
          body={t("shop.linkFinalWarningBody")}
          confirmLabel={t("shop.linkFinalConfirm")}
          busy={slugBusy}
          onCancel={() => setSlugConfirmStep(0)}
          onConfirm={submitSlug}
        />
      )}
    </div>
  );
};

/** Light-themed blocking confirm for the two-step slug-change warning — kept
 * local rather than reusing pos/components/ConfirmDialog, which is dark-themed
 * for the register and lives in a different subsystem. */
const SlugConfirmDialog = ({ title, body, confirmLabel, busy, onCancel, onConfirm }) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
      <button
        type="button"
        aria-label={t("common.cancel")}
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-2">{body}</p>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded-xl border border-gray-300 font-semibold hover:bg-gray-50 disabled:opacity-60"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("common.saving") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopProfile;
